import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Link2, Loader2, RefreshCw, Unlink, CheckCircle2, Database, ExternalLink, ChevronDown, ChevronUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { api } from "@/lib/api";

const SetupGuide = () => {
  const [open, setOpen] = useState(true);
  return (
    <div className="rounded-lg border border-zinc-800 bg-zinc-900/50" data-testid="notion-setup-guide">
      <button onClick={() => setOpen((v) => !v)} className="flex w-full items-center justify-between px-3.5 py-2.5 text-left">
        <span className="text-sm font-600 text-zinc-200">How to set it up (one time)</span>
        {open ? <ChevronUp className="h-4 w-4 text-zinc-400" /> : <ChevronDown className="h-4 w-4 text-zinc-400" />}
      </button>
      {open && (
        <ol className="list-decimal space-y-2 px-6 pb-4 text-xs leading-relaxed text-zinc-300">
          <li>In Notion, open <b>Settings</b> → <b>Connections</b> → tap <b>Develop or manage integrations</b> (Developer portal). Or just open <a href="https://www.notion.so/profile/integrations" target="_blank" rel="noreferrer" className="font-600 text-[#E0575B] underline">notion.so/profile/integrations</a>.</li>
          <li>In the portal, go to the <b>Connections</b> tab → <b>New connection</b>. Name it (e.g. "Stage Door"), pick your workspace, and save.</li>
          <li>Open your new connection and copy the <b>Integration token</b> (starts with <code className="rounded bg-zinc-800 px-1">ntn_</code>). Paste it below.</li>
          <li>Back in Notion, create a <b>brand-new empty page</b> (don't use one with important data). Name it anything, e.g. "Tours Backup".</li>
          <li>On that page, tap the <b>•••</b> menu (top-right) → <b>Connections</b> → <b>Connect to</b> → choose your integration.</li>
          <li>Copy the page's link (Share → Copy link) and paste it below.</li>
          <li>Tap <b>Connect</b>, then <b>Create backup database</b> — Stage Door builds the table and columns for you. Nothing else in your Notion is touched.</li>
        </ol>
      )}
    </div>
  );
};

export const NotionSync = ({ onSynced }) => {
  const [status, setStatus] = useState(null);
  const [token, setToken] = useState("");
  const [parent, setParent] = useState("");
  const [connecting, setConnecting] = useState(false);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    try { setStatus(await api.notionStatus()); } catch { /* ignore */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Poll while a backup is running.
  useEffect(() => {
    if (!status?.syncing) return;
    const t = setInterval(async () => {
      const s = await api.notionStatus();
      setStatus(s);
      if (!s.syncing) {
        clearInterval(t);
        onSynced && onSynced();
        if (s.last_result) toast.success(`Backup complete — ${s.last_result.created} added, ${s.last_result.updated} updated${s.last_result.failed ? `, ${s.last_result.failed} failed` : ""}`);
      }
    }, 2500);
    return () => clearInterval(t);
  }, [status?.syncing, onSynced]);

  const connect = async () => {
    if (!token.trim() || !parent.trim()) { toast.error("Enter your token and the Notion page link"); return; }
    setConnecting(true);
    try {
      await api.notionConnect({ token: token.trim(), parent: parent.trim() });
      toast.success("Connected to Notion");
      setToken(""); setParent("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.slice(0, 140) || "Could not connect (check the token + that the page is shared)");
    } finally {
      setConnecting(false);
    }
  };

  const createDb = async () => {
    setCreating(true);
    try {
      const res = await api.notionCreateDatabase();
      toast.success("Backup database created in Notion");
      await load();
      if (res.url) window.open(res.url, "_blank");
    } catch (e) {
      toast.error(e?.response?.data?.detail?.slice(0, 140) || "Could not create the database");
    } finally {
      setCreating(false);
    }
  };

  const sync = async (full) => {
    try { await api.notionSync(full); setStatus((s) => ({ ...s, syncing: true })); toast.info(full ? "Full backup started…" : "Backing up recent tours…"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Backup failed"); }
  };

  const disconnect = async () => {
    await api.notionDisconnect();
    toast.success("Notion disconnected");
    setStatus({ connected: false });
  };

  const toggleAuto = async (v) => {
    setStatus((s) => ({ ...s, auto_sync: v }));
    try { await api.notionSettings({ auto_sync: v }); } catch { /* ignore */ }
  };

  if (!status) return <p className="text-sm text-zinc-400">Loading…</p>;

  // Step 1: not connected — token + page link + guide.
  if (!status.connected) {
    return (
      <div className="space-y-3">
        <SetupGuide />
        <div>
          <Label className="mb-1.5 block text-xs uppercase tracking-wide text-zinc-400">Integration Token</Label>
          <Input type="password" value={token} onChange={(e) => setToken(e.target.value)} placeholder="ntn_…" className="border-zinc-800 bg-zinc-900 focus-visible:ring-[#E0575B]/70" data-testid="notion-token-input" />
        </div>
        <div>
          <Label className="mb-1.5 block text-xs uppercase tracking-wide text-zinc-400">Notion Page Link</Label>
          <Input value={parent} onChange={(e) => setParent(e.target.value)} placeholder="https://notion.so/your-empty-page…" className="border-zinc-800 bg-zinc-900 focus-visible:ring-[#E0575B]/70" data-testid="notion-parent-input" />
          <p className="mt-1 text-xs text-zinc-600">A new, empty page you shared with the integration. Stage Door creates the database on it.</p>
        </div>
        <Button onClick={connect} disabled={connecting} className="bg-[#9B2226] text-white hover:bg-[#7A1A1E]" data-testid="notion-connect-btn">
          {connecting ? <><Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> Connecting…</> : <><Link2 className="mr-1.5 h-4 w-4" /> Connect Notion</>}
        </Button>
      </div>
    );
  }

  // Step 2: connected but no database yet.
  if (!status.has_database) {
    return (
      <div className="space-y-3" data-testid="notion-needs-database">
        <div className="flex items-center gap-2 text-sm text-emerald-300">
          <CheckCircle2 className="h-4 w-4" /> Connected to your Notion page
        </div>
        <p className="text-sm text-zinc-300">One more step: create the backup database. Stage Door adds a fresh table (with all the columns) to your shared page.</p>
        <Button onClick={createDb} disabled={creating} className="bg-[#9B2226] text-white hover:bg-[#7A1A1E]" data-testid="notion-create-db-btn">
          {creating ? <><Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> Creating…</> : <><Database className="mr-1.5 h-4 w-4" /> Create backup database</>}
        </Button>
        <Button onClick={disconnect} variant="outline" className="ml-2 border-zinc-700 bg-transparent text-zinc-300 hover:bg-zinc-800" data-testid="notion-disconnect-btn">
          <Unlink className="mr-1.5 h-4 w-4" /> Disconnect
        </Button>
      </div>
    );
  }

  // Step 3: fully set up.
  return (
    <div className="space-y-3" data-testid="notion-connected">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <span className="inline-flex items-center gap-2 text-sm text-emerald-300"><CheckCircle2 className="h-4 w-4" /> Backup ready</span>
        {status.db_url && (
          <a href={status.db_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs font-600 text-[#E0575B] hover:underline" data-testid="notion-open-db">
            <ExternalLink className="h-3 w-3" /> Open in Notion
          </a>
        )}
      </div>
      {status.last_synced && <p className="text-xs text-zinc-400">Last backup: {new Date(status.last_synced).toLocaleString()}{status.last_result ? ` · ${status.last_result.created} added, ${status.last_result.updated} updated${status.last_result.failed ? `, ${status.last_result.failed} failed` : ""}${status.last_result.scope === "recent" ? " (recent)" : status.last_result.scope === "full" ? " (full)" : ""}` : ""}</p>}
      {status.error && <p className="text-xs text-red-400">Last error: {status.error}</p>}
      <div className="flex items-center justify-between rounded-lg border border-zinc-800 bg-zinc-900 px-3 py-2.5">
        <span className="text-sm text-zinc-300">Auto-back up recent tours every few hours</span>
        <Switch checked={!!status.auto_sync} onCheckedChange={toggleAuto} data-testid="notion-auto-toggle" />
      </div>
      <div className="flex flex-wrap gap-2">
        <Button onClick={() => sync(false)} disabled={status.syncing} className="bg-[#9B2226] text-white hover:bg-[#7A1A1E]" data-testid="notion-sync-btn">
          {status.syncing ? <><Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> Backing up…</> : <><RefreshCw className="mr-1.5 h-4 w-4" /> Back up recent</>}
        </Button>
        <Button onClick={() => sync(true)} disabled={status.syncing} variant="outline" className="border-zinc-700 bg-transparent text-zinc-200 hover:bg-zinc-800" data-testid="notion-full-sync-btn">
          <Database className="mr-1.5 h-4 w-4" /> Full backup
        </Button>
        <Button onClick={disconnect} variant="outline" className="border-zinc-700 bg-transparent text-zinc-300 hover:bg-zinc-800" data-testid="notion-disconnect-btn">
          <Unlink className="mr-1.5 h-4 w-4" /> Disconnect
        </Button>
      </div>
      <p className="text-xs text-zinc-600">Auto-backup keeps your 5 most recent tours current. Use <b>Full backup</b> to re-push everything (e.g. after editing an older tour). One-way only — Stage Door never changes your app data.</p>
    </div>
  );
};
